/** Shared planner-mode board column catalog and conf builders. */

import { retrieveCompanyData, truckStatusColumnEnabledFromCompany } from '../supplementary_components/companySettings';

export const PLANNER_COLUMN_CATALOG = [
    { name: 'truckNumber', label: 'Truck', locked: true, defaultOn: true, rem: 5.5 },
    { name: 'truckState', label: 'Sts', locked: false, defaultOn: true, rem: 2.2 },
    { name: 'drivers', label: 'Driver Name', locked: false, defaultOn: false, rem: 13 },
    { name: 'trailer', label: 'Trailer', locked: false, defaultOn: true, rem: 3.8 },
    { name: 'currentLocation', label: 'Location', locked: false, defaultOn: true, rem: 11 },
    { name: 'notes', label: 'Notes', locked: false, defaultOn: true, rem: 14, alignment: 'center' },
    { name: 'gross', label: 'Gross', locked: false, defaultOn: true, rem: 4.5, alignment: 'right', requiresRates: true },
    { name: 'rpm', label: 'RPM', locked: false, defaultOn: false, rem: 4, alignment: 'right', requiresRates: true },
    { name: 'payoutGross', label: 'Payout Gross', locked: false, defaultOn: false, rem: 5.5, alignment: 'right', requiresRates: true, requiresFollowsWeeklyRate: true, requiresDriverPayout: true },
    { name: 'payoutRpm', label: 'Payout RPM', locked: false, defaultOn: false, rem: 4.8, alignment: 'right', requiresRates: true, requiresFollowsWeeklyRate: true, requiresDriverPayout: true },
    { name: 'fuelLevel', label: 'Fuel Level', locked: false, defaultOn: false, rem: 2.2 },
    { name: 'truckStatus', label: 'Truck Status', locked: false, defaultOn: false, rem: 8, requiresTruckStatus: true },
];

/** Default for "Show Driver Payout Gross" (rendered under Gross; not a column). */
export const DEFAULT_SHOW_DRIVER_PAYOUT_GROSS = false;

/** Persistable preference (does not apply updater/rates gates). Missing/unset → off. */
export const normalizeShowDriverPayoutGross = (raw) => {
    if (raw === true || raw === 'true') return true;
    return DEFAULT_SHOW_DRIVER_PAYOUT_GROSS;
};

/**
 * Effective preference for showing driver payout under Gross.
 * Updaters never see it even if their saved pref is on (e.g. former dispatcher).
 * Flag-true companies use dedicated Payout Gross / Payout RPM columns instead.
 */
export const resolveShowDriverPayoutGross = (raw, { userIsUpdater = false, userCanSeeRates = true, followsWeeklyOwnerPayout = false } = {}) => {
    if (followsWeeklyOwnerPayout || userIsUpdater || !userCanSeeRates) return false;
    return normalizeShowDriverPayoutGross(raw);
};

export const followsWeeklyOwnerPayoutFromCompany = (company = retrieveCompanyData()) =>
    company?.ownerLeasePayoutFollowsWeeklyRateDistribution === true;

export const buildPlannerColumnAccessOpts = ({ userCanSeeRates, userIsUpdater } = {}) => {
    const company = retrieveCompanyData();
    return {
        userCanSeeRates,
        userIsUpdater,
        followsWeeklyOwnerPayout: followsWeeklyOwnerPayoutFromCompany(company),
        updatersCanSeeDriverPayout: company?.updatersCanSeeDriverPayout !== false,
        truckStatusColumnEnabled: truckStatusColumnEnabledFromCompany(company),
    };
};

export const DEFAULT_BOARD_STATISTICS = {
    activeTrucks: true,
    openTrucks: true,
    atHomeTrucks: true,
    totalGross: true,
    averageGross: false,
    averageRpm: false,
};

const COLUMN_GAP_REM = 0.3;
const SECTION_PADDING_X_REM = 1; // 0.5rem each side on truck card
/** Matches `.teamNameSection { width }` so header aligns with team strip + trucks. */
const TEAM_STRIP_REM = 1.75;

export const getVisiblePlannerColumnCatalog = ({
    userCanSeeRates,
    userIsUpdater,
    followsWeeklyOwnerPayout,
    updatersCanSeeDriverPayout,
    truckStatusColumnEnabled,
} = {}) => {
    const access = {
        userCanSeeRates,
        userIsUpdater,
        followsWeeklyOwnerPayout:
            followsWeeklyOwnerPayout === true ||
            (followsWeeklyOwnerPayout == null && followsWeeklyOwnerPayoutFromCompany()),
        updatersCanSeeDriverPayout:
            updatersCanSeeDriverPayout !== false &&
            (updatersCanSeeDriverPayout != null ||
                retrieveCompanyData()?.updatersCanSeeDriverPayout !== false),
        truckStatusColumnEnabled:
            truckStatusColumnEnabled === true ||
            (truckStatusColumnEnabled == null && truckStatusColumnEnabledFromCompany()),
    };
    return PLANNER_COLUMN_CATALOG.filter(
        (c) =>
            !(access.userIsUpdater && c.hideForUpdater) &&
            !(c.requiresRates && !access.userCanSeeRates) &&
            !(c.requiresFollowsWeeklyRate && !access.followsWeeklyOwnerPayout) &&
            !(c.requiresDriverPayout && access.userIsUpdater && !access.updatersCanSeeDriverPayout) &&
            !(c.requiresTruckStatus && !access.truckStatusColumnEnabled)
    );
};

export const getDefaultPlannerColumns = (accessOpts = {}) =>
    getVisiblePlannerColumnCatalog(accessOpts)
        .filter((c) => c.defaultOn)
        .map((c) => c.name);

/** Sanitize saved column list against catalog + access rules; always keep Truck first. */
export const normalizePlannerColumns = (rawColumns, accessOpts = {}) => {
    const catalog = getVisiblePlannerColumnCatalog(accessOpts);
    const allowed = new Set(catalog.map((c) => c.name));
    const defaults = getDefaultPlannerColumns(accessOpts);
    const followsWeekly =
        accessOpts.followsWeeklyOwnerPayout === true ||
        (accessOpts.followsWeeklyOwnerPayout == null && followsWeeklyOwnerPayoutFromCompany());
    if (!Array.isArray(rawColumns) || rawColumns.length === 0) {
        return defaults;
    }
    const seen = new Set();
    const next = [];
    for (const name of rawColumns) {
        // Flag false: payoutGross was a toggle under Gross, not a column.
        if (!followsWeekly && (name === 'payoutGross' || name === 'payoutRpm')) continue;
        // Keep Truck Status in the saved list even when this company has the feature off,
        // so saving settings here does not remove it for a company that has it on.
        if (name === 'truckStatus') {
            if (!seen.has(name)) {
                seen.add(name);
                next.push(name);
            }
            continue;
        }
        if (!allowed.has(name) || seen.has(name)) continue;
        seen.add(name);
        next.push(name);
    }
    if (next[0] !== 'truckNumber') {
        const withoutTruck = next.filter((n) => n !== 'truckNumber');
        next.length = 0;
        next.push('truckNumber', ...withoutTruck);
    }
    if (!next.includes('truckNumber')) {
        next.unshift('truckNumber');
    }
    return next.length > 0 ? next : defaults;
};

/** Columns to render. Hides Truck Status when this company has the feature turned off. */
export const columnsForBoard = (rawColumns, accessOpts = {}) => {
    const normalized = normalizePlannerColumns(rawColumns, accessOpts);
    const featureOn = accessOpts.truckStatusColumnEnabled === true
        || (accessOpts.truckStatusColumnEnabled == null && truckStatusColumnEnabledFromCompany());
    if (featureOn) return normalized;
    return normalized.filter((name) => name !== 'truckStatus');
};

const REGULAR_BOARD_TRACKS = [
    { name: 'truckNumber', fr: '0.5fr', headerFr: '0.43fr' },
    { name: 'drivers', fr: '0.9fr', headerFr: '0.96fr' },
    { name: 'trailer', fr: '0.3fr', headerFr: '0.33fr' },
    { name: 'status', fr: '0.8fr', headerFr: '0.73fr', sortable: true },
    { name: 'currentLocation', fr: '0.9fr', headerFr: '0.87fr' },
    { name: 'destination', fr: '0.9fr', headerFr: '0.89fr' },
    { name: 'currentLoadNumber', fr: '0.7fr', headerFr: '0.7fr' },
    { name: 'fuelLevel', fr: '0.4fr', headerFr: '0.4fr' },
    { name: 'notes', fr: '3.1fr', headerFr: '3.1fr', alignment: 'center' },
    { name: 'gross', fr: '0.4fr', headerFr: '0.4fr', alignment: 'right', requiresRates: true },
];

/** Regular (non-planner) board. Truck Status is inserted when the user added that column. */
export const buildRegularBoardConf = (userCanSeeLoadRates, boardColumns) => {
    const tracks = REGULAR_BOARD_TRACKS
        .filter((track) => !track.requiresRates || userCanSeeLoadRates)
        .map((track) => ({ ...track }));
    if (Array.isArray(boardColumns) && boardColumns.includes('truckStatus')) {
        const names = tracks.map((track) => track.name);
        let after = 'truckNumber';
        const statusIndex = boardColumns.indexOf('truckStatus');
        for (let i = statusIndex - 1; i >= 0; i--) {
            if (names.includes(boardColumns[i])) {
                after = boardColumns[i];
                break;
            }
        }
        tracks.splice(names.indexOf(after) + 1, 0, { name: 'truckStatus', fr: '0.95fr', headerFr: '0.95fr' });
    }
    const columns = tracks.map((track) => {
        const col = { name: track.name };
        if (track.alignment) col.alignment = track.alignment;
        if (track.sortable) col.sortable = true;
        return col;
    });
    return {
        columnProportions: `1.75rem ${tracks.map((track) => track.fr).join(' ')}`,
        headerColumnProportions: `1.75rem ${tracks.map((track) => track.headerFr).join(' ')}`,
        columns,
    };
};

export const normalizeBoardStatistics = (raw, { userCanSeeRates = true } = {}) => {
    const source = raw && typeof raw === 'object' ? raw : {};
    const next = { ...DEFAULT_BOARD_STATISTICS };
    for (const key of Object.keys(DEFAULT_BOARD_STATISTICS)) {
        if (source[key] !== undefined) {
            next[key] = source[key] !== false && source[key] !== 'false';
        }
    }
    if (!userCanSeeRates) {
        next.totalGross = false;
        next.averageGross = false;
        next.averageRpm = false;
    }
    return next;
};

export const normalizeHorizontalScrollTimeline = (raw) =>
    raw !== false && raw !== 'false';

const catalogByName = (() => {
    const map = {};
    for (const col of PLANNER_COLUMN_CATALOG) map[col.name] = col;
    return map;
})();

export const computePlannerSectionWidthRem = (columnNames) => {
    const names = columnNames && columnNames.length ? columnNames : ['truckNumber'];
    const colsSum = names.reduce((sum, name) => sum + (catalogByName[name]?.rem || 4), 0);
    const gaps = Math.max(0, names.length - 1) * COLUMN_GAP_REM;
    return colsSum + gaps + SECTION_PADDING_X_REM;
};

/**
 * Build MAIN_BOARD_CONF for planner mode from ordered column names.
 * Header width matches grid tracks + gaps (no trailing dead space).
 */
export const buildPlannerBoardConf = (columnNames) => {
    const names = columnNames && columnNames.length ? columnNames : ['truckNumber'];
    const remParts = names.map((name) => `${catalogByName[name]?.rem || 4}rem`);
    // TruckCard strips the first token; keep a leading placeholder for that convention.
    const columnProportions = `${TEAM_STRIP_REM}rem ${remParts.join(' ')}`;
    // Header skips the GenerateHeaderConf spacing column — data tracks only.
    const headerColumnProportions = remParts.join(' ');
    const colsSum = names.reduce((sum, name) => sum + (catalogByName[name]?.rem || 4), 0);
    const dataGaps = Math.max(0, names.length - 1) * COLUMN_GAP_REM;
    const sectionWidthRem = colsSum + dataGaps + SECTION_PADDING_X_REM;
    const headerSectionWidthRem = TEAM_STRIP_REM + sectionWidthRem;
    const columns = names.map((name) => {
        const meta = catalogByName[name] || { name };
        const col = { name };
        if (meta.alignment) col.alignment = meta.alignment;
        return col;
    });
    return {
        columnProportions,
        headerColumnProportions,
        columns,
        sectionWidthRem,
        headerSectionWidthRem,
        headerColumnGap: `${COLUMN_GAP_REM}rem`,
        headerCompactPadding: true,
        headerPaddingLeftRem: TEAM_STRIP_REM,
    };
};

/**
 * Truck weekly RPM using the same rules as WeeklyPerformanceSection.
 * Returns null when RPM is N/A / not calculable.
 */
export const loadHasBoardShare = (loadData) => {
    if (!loadData || typeof loadData !== 'object') return false;
    const pct = loadData.percentageOfTheLoad;
    if (pct === 0 || pct === '0') return false;
    return true;
};

export const calculateTruckWeeklyRpm = (loadsList) => {
    const totals = accumulateWeeklyGrossAndMiles(loadsList);
    if (!totals || totals.loadWithoutMileage || totals.totalMiles === 0) return null;
    return totals.totalGross / totals.totalMiles;
};

export const explicitPayoutAmountByLoadId = (thisWeeksDriverPayoutLoads) => {
    const payoutById = new Map();
    if (!Array.isArray(thisWeeksDriverPayoutLoads)) return payoutById;
    for (const entry of thisWeeksDriverPayoutLoads) {
        if (entry == null) continue;
        const data = typeof entry === 'string' ? JSON.parse(entry) : entry;
        if (data?.id == null) continue;
        if (data.driverPayoutAmount != null && data.driverPayoutAmount !== '') {
            payoutById.set(Number(data.id), Number(data.driverPayoutAmount) || 0);
        }
    }
    return payoutById;
};

export const dollarAmountsDiffer = (a, b) => Math.round(Number(a) * 100) !== Math.round(Number(b) * 100);

/** Flag-true Payout Gross: cache amount when present (including $0), else invoice slice. */
export const calculateJoinedPayoutGross = (thisWeeksLoads, thisWeeksDriverPayoutLoads) => {
    if (!thisWeeksLoads || !Array.isArray(thisWeeksLoads)) return 0;
    const payoutById = explicitPayoutAmountByLoadId(thisWeeksDriverPayoutLoads);
    let total = 0;
    for (const load of thisWeeksLoads) {
        if (load == null) continue;
        const loadData = typeof load === 'string' ? JSON.parse(load) : load;
        if (!loadHasBoardShare(loadData)) continue;
        const id = Number(loadData.id);
        if (payoutById.has(id)) {
            total += payoutById.get(id) || 0;
        } else {
            total += Number(loadData.amountForInvoice) || 0;
        }
    }
    return total;
};

export const calculateTruckPayoutRpm = (thisWeeksLoads, thisWeeksDriverPayoutLoads) => {
    const totals = accumulateWeeklyGrossAndMiles(thisWeeksLoads);
    if (!totals || totals.loadWithoutMileage || totals.totalMiles === 0) return null;
    const payoutGross = calculateJoinedPayoutGross(thisWeeksLoads, thisWeeksDriverPayoutLoads);
    return payoutGross / totals.totalMiles;
};

export function accumulateWeeklyGrossAndMiles(loadsList) {
    if (!loadsList || loadsList.length === 0) return null;
    let totalGross = 0;
    let totalMiles = 0;
    let loadWithoutMileage = false;
    for (const load of loadsList) {
        if (load == null) continue;
        const loadData = typeof load === 'string' ? JSON.parse(load) : load;
        if (!loadHasBoardShare(loadData)) continue;
        const amount = loadData.amountForInvoice != null ? Number(loadData.amountForInvoice) || 0 : 0;
        totalGross += amount;
        if (loadData.loadStatus !== 'Cancelled' && loadData.mileage === 0 && amount !== 0) {
            loadWithoutMileage = true;
        }
        if (loadData.loadStatus === 'Cancelled' && amount > 0) {
            totalMiles += (Number(loadData.deadhead) || 0) * (loadData.percentageOfTheLoad || 1);
        } else {
            totalMiles +=
                amount > 0
                    ? ((Number(loadData.mileage) || 0) + (Number(loadData.deadhead) || 0)) *
                      (loadData.percentageOfTheLoad || 1)
                    : 0;
        }
    }
    if (loadWithoutMileage || totalGross === 0) totalMiles = 0;
    return { totalGross, totalMiles, loadWithoutMileage };
}
